import React from "react"
import { Modal, Form, Col, Button } from "react-bootstrap"
import { FaAngleDown, FaPencilAlt, FaTimes } from "react-icons/fa"
import { toast } from "react-toastify"
import { Subscription } from "rxjs"
import { APIError } from "../../api/ObservableFromFetch"
import ActionButton from "../ActionButton"
import { Typeahead } from "react-bootstrap-typeahead"
import GovDevice, { ModifyGovDevice } from "../../api/Randoms/GovPoolModel/GovDevice"
import LabAccount from "../../api/Administration/Model/LabAccount"
import { DevicesGetObservable, GovDeviceModifyObservable } from "../../api/Randoms/GovDevicesGet"
import FormatDate from "../../formatters/FormatDate"

interface DeviceModalProps {
  addNew?: boolean
  labAccount?: LabAccount
  currentDevices: number[]
  details?: GovDevice
  onHide: () => void
  onSave: () => void
}

interface DeviceModalState {
  isDeviceValid?: boolean
  devices: GovDevice[]
  selectedDevice?: GovDevice
  loading: boolean
  saving: boolean
  editing: boolean
}

export default class DeviceModal extends React.PureComponent<DeviceModalProps, DeviceModalState> {
  state: DeviceModalState = {
    loading: false,
    saving: false,
    editing: false,
    devices: []
  }

  loadSubscription?: Subscription
  saveSubscription?: Subscription
  deviceTypeahead = React.createRef<Typeahead<GovDevice>>()

  componentDidMount() {
    this.setState({...this.defaultStateFromProps(), loading: true})

    this.loadSubscription = DevicesGetObservable().subscribe(
      data  => this.setState({devices: this.filterDevices(this.props.labAccount?.LabAccountNumber || "", data), selectedDevice: data.find(el => el.DeviceID === this.props.details?.DeviceID), loading: false}),
      error => this.handleAPIError(error, { loading: false})
    )
  }

  componentWillUnmount() {
    this.loadSubscription && this.loadSubscription.unsubscribe()
    this.saveSubscription && this.saveSubscription.unsubscribe()
  }

  handleAPIError = (error: Error | APIError, stateChanges: object) => {
    this.setState(stateChanges)
    error && toast.error((error as APIError)?.errorData?.message || error.message)
  }

  onCancel = () => this.props.onHide()

  onSubmit = () => {
    this.setState({saving: true})
    const selectedDevice = this.state.selectedDevice
    const data: ModifyGovDevice = {
      DeviceID: selectedDevice?.DeviceID || 0,
      LabAccount: this.props.labAccount?.LabAccountNumber || "",
      DeviceLabAccountXrefID: this.props.details?.DeviceLabAccountXrefID || 0,
      Delete: false
    }

    this.saveSubscription = GovDeviceModifyObservable(data).subscribe(
      data => {
        this.setState({ saving: false })
        toast.success(data)
        this.props.onSave()
      },
      error => this.handleAPIError(error, { saving: false })
    )
  }

  valueText = (text: string | number | undefined) => <Col xs={6} md={8} className="col-form-label">{text}</Col>
  rowClassName = (required: boolean = false) => required ? "form-group required" : "form-group"

  isValid = () => !!this.state.selectedDevice

  handleToggleEditMode = () => {
    this.setState({editing: !this.state.editing, ...this.defaultStateFromProps()})
  }

  defaultStateFromProps = () => {
    const data = this.props.details
    return {
      selectedDevice: this.state.devices.find(el => el.DeviceID === data?.DeviceID)
    }
  }

  handleDeviceChange = (selected: GovDevice[]) => {
    this.setState({selectedDevice: selected[0]})
  }

  filterDevices = (labAccount: string, data: GovDevice[]) => {
    let filteredDevices = data
    if (labAccount.split(".")[1].includes("CON")) {
      filteredDevices = data.filter(({SampleType}) => SampleType === "UR")
    }
    if (labAccount.split(".")[1].includes("OCO")) {
      filteredDevices = data.filter(({SampleType}) => SampleType === "SL")
    }
    return filteredDevices.filter(el => !this.props.currentDevices.includes(el.DeviceID))
  }

  render() {
    const { loading, saving, selectedDevice, devices } = this.state
    const editing = this.state.editing || !!this.props.addNew

    return (
      <Modal backdrop="static" show size="lg" dialogClassName="dark">
        <Modal.Header>
          {!!this.props.addNew && <Modal.Title className="text-uppercase">Add New Device</Modal.Title>}
          {!editing && <Modal.Title className="text-uppercase">Device</Modal.Title>}
          {editing && !this.props.addNew && <Modal.Title className="text-uppercase">Edit Device</Modal.Title>}
        </Modal.Header>
        <Modal.Body>
          <Form className="px-4">
            {!this.props.addNew && false && <Form.Row>
              <Col><h5>MODIFY DEVICE</h5></Col>
              <Col sm={1} className={"text-right"}>
                <h5>
                  <Button variant="icon-link" size="sm" className="float-right mb-3 btn-icon-primary" onClick={this.handleToggleEditMode}>
                    {!this.state.editing && <FaPencilAlt />}
                    {this.state.editing && <FaTimes />}
                  </Button>
                </h5>
              </Col>
            </Form.Row>}
            <Form.Row className={this.rowClassName()}>
              <Form.Label column xs={6} md={4}>Device</Form.Label>
              {!editing && this.valueText(this.props.details?.Description)}
              {editing &&
                <Col xs={6} md={8}>
                  <Typeahead id="device"
                    ref={this.deviceTypeahead}
                    labelKey="Description"
                    emptyLabel="No Devices Found"
                    disabled={loading}
                    filterBy={(option, props) => props.selected.length > 0 || option.Description.toLowerCase().indexOf(props.text.toLowerCase()) !== -1}
                    onChange={this.handleDeviceChange}
                    options={devices || []}
                    selected={!!selectedDevice ? [selectedDevice] : []}
                    placeholder="Enter the Device">
                    <Button variant="toggle"
                      disabled={loading}
                      onClick={() => { this.deviceTypeahead.current?.toggleMenu() }}>
                      <FaAngleDown />
                    </Button>
                  </Typeahead>
                </Col>}
            </Form.Row>
            <Form.Row className={this.rowClassName()}>
              <Form.Label column xs={6} md={4}>Code</Form.Label>
              {this.valueText(selectedDevice?.Code)}
            </Form.Row>
            <Form.Row className={this.rowClassName()}>
              <Form.Label column xs={6} md={4}>Sample Type</Form.Label>
              {this.valueText(selectedDevice?.SampleType)}
            </Form.Row>
            <Form.Row className={this.rowClassName()}>
              <Form.Label column xs={6} md={4}>Begin Effective Date</Form.Label>
              {this.valueText(FormatDate(selectedDevice?.BeginEffectiveDate))}
            </Form.Row>
            <Form.Row className={this.rowClassName()}>
              <Form.Label column xs={6} md={4}>End Effective Date</Form.Label>
              {this.valueText(FormatDate(selectedDevice?.EndEffectiveDate))}
            </Form.Row>
            <Form.Row className={this.rowClassName()}>
              <Form.Label column xs={6} md={4}>Manufacturer</Form.Label>
              {this.valueText(selectedDevice?.Manufacturer)}
            </Form.Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={ this.onCancel } className="px-4">{editing ? "Cancel" : "Close"}</Button>
          {editing && <ActionButton variant="primary" onClick={ this.onSubmit } disabled={loading || !this.isValid()} spin={saving} disableOnSpin>{!!this.props.addNew ? "Save" : "Update"}</ActionButton>}
        </Modal.Footer>
      </Modal>
    )
  }
}
